import {
  Alert, Box, Button, ColumnLayout, Container, Header, SpaceBetween,
} from '@cloudscape-design/components';
import { deriveSlug } from '../fmBundles';
import { FmWizardState, NEW_ENV_VALUE } from './types';

interface EnvOption { documentId: string; name: string; isProduction: boolean; }

interface Props {
  state: FmWizardState;
  environments: EnvOption[];
  submitError: string | null;
  credsMissing: string | null;
  onEdit: (step: number) => void;
}

function Value({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>;
}

export default function ReviewStep({ state, environments, submitError, credsMissing, onEdit }: Props) {
  const projectSlug = deriveSlug(state.projectName);
  const envLabel = state.environmentId === NEW_ENV_VALUE
    ? `${state.newEnvName} (new${state.newEnvIsProduction ? ', production' : ''})`
    : environments.find((e) => e.documentId === state.environmentId)?.name ?? '—';
  const budgetLabel = `${state.budgetPeriod === 'monthly' ? 'Monthly' : 'Yearly'}: $${state.budgetAmount}`;
  const emails = state.businessEmails.filter((e) => e.trim());
  const roles = state.computeRoles.filter((r) => r.trim());

  return (
    <SpaceBetween size="l">
      {credsMissing && (
        <Alert type="error" header="AWS credentials required">
          {credsMissing}
        </Alert>
      )}
      {submitError && <Alert type="error" header="Submission failed">{submitError}</Alert>}

      <Container header={<Header variant="h3" actions={<Button onClick={() => onEdit(0)}>Edit</Button>}>Bundle info</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <div key="project"><Box variant="awsui-key-label">Project</Box><Value>{state.projectName || '—'}</Value></div>
          <div key="env"><Box variant="awsui-key-label">Environment</Box><Value>{envLabel}</Value></div>
          <div key="team"><Box variant="awsui-key-label">Team</Box><Value>{state.team || '—'}</Value></div>
          <div key="slug"><Box variant="awsui-key-label">Project slug</Box><Value>{projectSlug || '—'}</Value></div>
          <div key="service"><Box variant="awsui-key-label">Service name</Box><Value>{state.serviceName || '—'}</Value></div>
          <div key="projectname">
            <Box variant="awsui-key-label">Runbook ProjectName / ICLProject tag</Box>
            <Value>{state.serviceName || '—'}</Value>
          </div>
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h3" counter={`(${state.models.length})`} actions={<Button onClick={() => onEdit(1)}>Edit</Button>}>Models</Header>}>
        <SpaceBetween size="xs">
          {state.models.map((m) => (
            <div key={m.modelId}>{m.shortName} — {m.modelName} ({m.provider})</div>
          ))}
          {state.models.length === 0 && <Box color="text-body-secondary">No models</Box>}
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h3" counter={`(${roles.length})`} actions={<Button onClick={() => onEdit(2)}>Edit</Button>}>Compute roles</Header>}>
        <SpaceBetween size="xs">
          {roles.map((r, i) => <div key={i}>{r}</div>)}
          {roles.length === 0 && <Box color="text-body-secondary">No compute roles</Box>}
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h3" actions={<Button onClick={() => onEdit(3)}>Edit</Button>}>Budget & notifications</Header>}>
        <SpaceBetween size="xs">
          <div key="budget"><Box variant="awsui-key-label">Budget</Box><Value>{budgetLabel}</Value></div>
          <div key="emails">
            <Box variant="awsui-key-label">Business emails</Box>
            <Value>{emails.length ? emails.join(', ') : '—'}</Value>
          </div>
          <Box color="text-body-secondary">The DevOps team is notified automatically.</Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
